"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Question,
    Recommencer,
    Resultat,
    dateLisible,
    useParcours,
} from "./Journey";
import { obligations } from "@/lib/tools/electronicInvoicing";

/**
 * Facturation électronique : ce qui s'impose à vous, et à quelle date.
 *
 * TROIS OBLIGATIONS, TROIS RÉPONSES DIFFÉRENTES, et c'est tout l'intérêt de
 * l'outil : presque tous les articles les mélangent en une seule date.
 *
 * Recevoir s'impose depuis le 1er septembre 2026 à toute entreprise, quelle
 * que soit sa taille et quelle que soit sa clientèle, parce que l'obligation
 * ne vient pas de soi mais de ses fournisseurs. Émettre attend le 1er septembre
 * 2027 pour une micro-entreprise. L'e-reporting ne concerne que ceux qui
 * vendent à des particuliers ou hors de France.
 *
 * LA FRANCHISE EN BASE NE DISPENSE DE RIEN, et c'est le contresens le plus
 * répandu : ne pas facturer de TVA n'empêche pas d'être assujetti à la TVA au
 * sens du code, et la réforme vise les assujettis.
 */

const PHASES = { taille: 0, pros: 1, particuliers: 1, etranger: 1, resultat: 2 };

const L = {
    fr: {
        phases: ["Votre entreprise", "Vos clients", "Votre calendrier"],
        retour: "Retour",
        oui: "Oui",
        non: "Non",
        recommencer: "Refaire le diagnostic",

        qTaille: "Quelle est la taille de votre entreprise ?",
        qTailleAide:
            "Elle ne change rien à l'obligation de recevoir, qui s'impose à tout le monde à la même date. Elle décide en revanche de la date à laquelle vous devrez émettre.",
        micro: "Micro-entreprise ou TPE",
        microDetail: "Moins de 10 personnes",
        pme: "PME",
        pmeDetail: "Moins de 250 personnes",
        eti: "ETI ou grande entreprise",
        etiDetail: "250 personnes et plus",

        qPros: "Facturez-vous des professionnels établis en France ?",
        qProsAide:
            "C'est le champ de la facturation électronique proprement dite : la facture circule de plateforme à plateforme, entre deux entreprises françaises.",
        prosOui: "Oui",
        prosOuiDetail: "Entreprises, et associations assujetties à la TVA",
        prosNon: "Non",
        prosNonDetail: "Aucun client professionnel français",

        qParticuliers: "Vendez-vous à des particuliers ?",
        qParticuliersAide:
            "Ces ventes sortent du champ de la facturation électronique, mais entrent dans celui de l'e-reporting : ce sont les données de l'opération qui partent à l'administration, pas la facture.",

        qEtranger: "Avez-vous des clients hors de France ?",
        qEtrangerAide:
            "Professionnels ou particuliers, dans l'Union européenne ou ailleurs. Ces opérations relèvent elles aussi de l'e-reporting.",

        resultatTitre: "Votre calendrier",
        enVigueurDepuis: "En vigueur depuis le {date}",
        depuisJours: "soit depuis {n} jours",
        dansJours: "Dans {n} jours, le {date}",
        nonConcerne: "Ne vous concerne pas",

        receptionTitre: "Recevoir vos factures au format électronique",
        receptionTexte:
            "Elle s'impose à toutes les entreprises à la même date, quelle que soit leur taille et même si elles ne facturent que des particuliers, parce qu'elle vient de vos fournisseurs, qui basculent les uns après les autres. Il vous faut pour cela une plateforme agréée : c'est elle qui reçoit vos factures d'achat.",

        emissionTitre: "Émettre vos factures au format électronique",
        emissionTexte:
            "Dès cette date, chaque facture adressée à un client professionnel établi en France se dépose sur une plateforme agréée, dans un format que les logiciels lisent seuls. Un PDF joint à un courriel ne remplit pas l'obligation, même signé et horodaté.",
        emissionNonConcerne:
            "Vous n'avez pas de clients professionnels français : cette obligation ne vous vise pas aujourd'hui. Elle s'appliquerait dès votre première facture à une entreprise établie en France.",

        ereportingTitre: "Transmettre vos données de transaction",
        ereportingTexte:
            "Quand vous vendez à un particulier ou à un client installé hors de France, la réforme ne vous demande pas de facture électronique, mais un relevé de ces ventes, que votre plateforme agréée transmet à l'administration fiscale. Le calendrier est celui de l'émission.",
        ereportingNonConcerne:
            "Vous ne vendez ni à des particuliers ni hors de France : l'e-reporting ne vous vise pas. Il s'appliquerait dès votre première vente dans l'un de ces deux cas.",

        franchiseTitre: "La franchise en base ne vous dispense pas de la réforme",
        franchiseTexte:
            "En franchise, vous ne facturez pas de TVA, mais vous y restez assujetti au sens du code, et la réforme s'adresse aux assujettis. Vos factures garderont la mention d'absence de TVA et suivront le même circuit que les autres.",
        plateformeTitre: "Une plateforme agréée est obligatoire",
        plateformeTexte:
            "Vous ne remettez plus la facture vous-même : vous la déposez sur la plateforme agréée que vous avez désignée, qui la transmet à celle de votre client. Certains logiciels de facturation en intègrent une, d'autres non.",
        note: "Calendrier revérifié le 8 septembre 2026 sur impots.gouv.fr et sur entreprendre.service-public.gouv.fr, qui concordent. Il a été reporté une fois, du 1er juillet 2024 au 1er septembre 2026 : vérifiez la date affichée par l'administration avant tout engagement contractuel avec un prestataire.",
    },
    en: {
        phases: ["Your business", "Your clients", "Your timetable"],
        retour: "Back",
        oui: "Yes",
        non: "No",
        recommencer: "Start over",

        qTaille: "How large is your business?",
        qTailleAide:
            "It changes nothing about the duty to receive, which applies to everyone on the same date. It does decide when you will have to issue.",
        micro: "Micro-business or very small business",
        microDetail: "Fewer than 10 people",
        pme: "Small or medium business",
        pmeDetail: "Fewer than 250 people",
        eti: "Mid-sized or large company",
        etiDetail: "250 people and above",

        qPros: "Do you invoice businesses established in France?",
        qProsAide:
            "That is the scope of electronic invoicing proper: the invoice travels from platform to platform, between two French businesses.",
        prosOui: "Yes",
        prosOuiDetail: "Companies, and charities registered for VAT",
        prosNon: "No",
        prosNonDetail: "No French business clients",

        qParticuliers: "Do you sell to consumers?",
        qParticuliersAide:
            "These sales fall outside electronic invoicing but inside e-reporting: it is the transaction data that goes to the tax authority, not the invoice.",

        qEtranger: "Do you have clients outside France?",
        qEtrangerAide:
            "Businesses or consumers, in the European Union or beyond. These transactions also fall under e-reporting.",

        resultatTitre: "Your timetable",
        enVigueurDepuis: "In force since {date}",
        depuisJours: "that is {n} days ago",
        dansJours: "In {n} days, on {date}",
        nonConcerne: "Does not apply to you",

        receptionTitre: "Receiving your invoices in electronic format",
        receptionTexte:
            "It applies to every business on the same date, whatever its size and even if it only invoices consumers, because it comes from your suppliers, who switch over one after another. You need an approved platform for this: it is the one that receives your purchase invoices.",

        emissionTitre: "Issuing your invoices in electronic format",
        emissionTexte:
            "From that date, every invoice to a business client established in France goes through an approved platform, in a format software can read on its own. A PDF attached to an email does not meet the duty, even signed and timestamped.",
        emissionNonConcerne:
            "You have no French business clients: this duty does not target you today. It would apply from your first invoice to a business established in France.",

        ereportingTitre: "Transmitting your transaction data",
        ereportingTexte:
            "When you sell to a consumer or to a client based outside France, the reform does not ask for an electronic invoice but for a record of those sales, which your approved platform passes on to the tax authority. The timetable is the same as for issuing.",
        ereportingNonConcerne:
            "You sell neither to consumers nor outside France: e-reporting does not target you. It would apply from your first sale in either case.",

        franchiseTitre: "The VAT exemption does not take you out of the reform",
        franchiseTexte:
            "Under the exemption you charge no VAT, but you remain a taxable person under the code, and the reform applies to taxable persons. Your invoices will keep their no-VAT wording and go through the same channel as the others.",
        plateformeTitre: "An approved platform is compulsory",
        plateformeTexte:
            "You no longer hand the invoice over yourself: you file it on the approved platform you have designated, which passes it on to your client's. Some invoicing software includes one, some does not.",
        note: "Timetable re-checked on 8 September 2026 against impots.gouv.fr and entreprendre.service-public.gouv.fr, which agree. It was postponed once, from 1 July 2024 to 1 September 2026: check the date shown by the authority before committing contractually to any provider.",
    },
};

/** Une obligation, son échéance et ce qu'elle demande. */
function Obligation({ titre, texte, statut }) {
    const ton = statut.concerne ? (statut.enVigueur ? "urgent" : "attente") : "hors";
    return (
        <div
            className={`rounded-xl p-5 border ${
                ton === "urgent"
                    ? "border-amber-500/50 bg-amber-500/[0.06]"
                    : ton === "attente"
                      ? "border-primary/40 bg-primary/[0.06]"
                      : "border-gray-200/70 dark:border-white/10"
            }`}
        >
            <div className="flex items-start gap-2.5">
                <SolarIcon
                    name={
                        ton === "urgent"
                            ? "danger-triangle-linear"
                            : ton === "attente"
                              ? "calendar-linear"
                              : "close-circle-linear"
                    }
                    className={`w-6 h-6 shrink-0 mt-0.5 ${
                        ton === "urgent"
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-400 dark:text-lightBlue/60"
                    }`}
                />
                <div className="min-w-0">
                    <div className="font-bold dark:text-white leading-snug">{titre}</div>
                    <div
                        className={`text-sm font-semibold mt-1 ${
                            ton === "urgent"
                                ? "text-amber-600 dark:text-amber-400"
                                : ton === "attente"
                                  ? "text-primary"
                                  : "text-gray-500 dark:text-lightBlue/70"
                        }`}
                    >
                        {statut.libelle}
                    </div>
                </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">{texte}</p>
        </div>
    );
}

export default function QuizFacturationElectronique({ lang = "fr", aujourdhui = null }) {
    const t = L[lang] || L.fr;
    const maintenant = useMemo(
        () => (aujourdhui ? new Date(aujourdhui) : new Date("2026-09-06T00:00:00Z")),
        [aujourdhui],
    );

    const [taille, setTaille] = useState("micro");
    const [clientsPros, setClientsPros] = useState(true);
    const [clientsParticuliers, setClientsParticuliers] = useState(false);
    const [clientsEtrangers, setClientsEtrangers] = useState(false);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "taille",
        () => {
            setTaille("micro");
            setClientsPros(true);
            setClientsParticuliers(false);
            setClientsEtrangers(false);
        },
        PHASES,
    );

    const lignes = useMemo(
        () =>
            obligations({
                taille,
                clientsPros,
                clientsParticuliers,
                clientsEtrangers,
                aujourdhui: maintenant,
            }),
        [taille, clientsPros, clientsParticuliers, clientsEtrangers, maintenant],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "taille") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.taille}
                titre={t.qTaille}
                aide={t.qTailleAide}
                choix={["micro", "pme", "eti"].map((cle) => ({
                    icone: {
                        micro: "user-rounded-linear",
                        pme: "users-group-rounded-linear",
                        eti: "buildings-2-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setTaille(cle);
                        aller("pros");
                    },
                }))}
            />
        );
    }

    if (etape === "pros") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.pros}
                titre={t.qPros}
                aide={t.qProsAide}
                choix={[
                    {
                        icone: "check-circle-linear",
                        titre: t.prosOui,
                        detail: t.prosOuiDetail,
                        onClick: () => {
                            setClientsPros(true);
                            aller("particuliers");
                        },
                    },
                    {
                        icone: "close-circle-linear",
                        titre: t.prosNon,
                        detail: t.prosNonDetail,
                        onClick: () => {
                            setClientsPros(false);
                            aller("particuliers");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "particuliers") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.particuliers}
                titre={t.qParticuliers}
                aide={t.qParticuliersAide}
                choix={[
                    {
                        icone: "check-circle-linear",
                        titre: t.oui,
                        onClick: () => {
                            setClientsParticuliers(true);
                            aller("etranger");
                        },
                    },
                    {
                        icone: "close-circle-linear",
                        titre: t.non,
                        onClick: () => {
                            setClientsParticuliers(false);
                            aller("etranger");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "etranger") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.etranger}
                titre={t.qEtranger}
                aide={t.qEtrangerAide}
                choix={[
                    {
                        icone: "global-linear",
                        titre: t.oui,
                        onClick: () => {
                            setClientsEtrangers(true);
                            aller("resultat");
                        },
                    },
                    {
                        icone: "close-circle-linear",
                        titre: t.non,
                        onClick: () => {
                            setClientsEtrangers(false);
                            aller("resultat");
                        },
                    },
                ]}
            />
        );
    }

    const statut = (cle) => {
        const o = lignes.find((x) => x.cle === cle);
        const date = dateLisible(new Date(`${o.date}T00:00:00Z`), lang);
        return {
            ...o,
            libelle: !o.concerne
                ? t.nonConcerne
                : o.enVigueur
                  ? `${t.enVigueurDepuis.replace("{date}", date)}, ${t.depuisJours.replace("{n}", Math.abs(o.jours))}`
                  : t.dansJours.replace("{n}", o.jours).replace("{date}", date),
        };
    };

    const reception = statut("reception");
    const emission = statut("emission");
    const ereporting = statut("ereporting");

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="space-y-3 mt-4">
                <Obligation
                    titre={t.receptionTitre}
                    texte={t.receptionTexte}
                    statut={reception}
                />
                <Obligation
                    titre={t.emissionTitre}
                    texte={emission.concerne ? t.emissionTexte : t.emissionNonConcerne}
                    statut={emission}
                />
                <Obligation
                    titre={t.ereportingTitre}
                    texte={ereporting.concerne ? t.ereportingTexte : t.ereportingNonConcerne}
                    statut={ereporting}
                />
            </div>

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white">{t.franchiseTitre}</div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-0">
                    {t.franchiseTexte}
                </p>
                <div className="text-sm font-semibold dark:text-white mt-4">
                    {t.plateformeTitre}
                </div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-0">
                    {t.plateformeTexte}
                </p>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.note}</p>

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
